import React, { useState, useEffect } from "react";

import CovertButton from "@cayuse/shared-react.components.covert-button";
import Icon from "@cayuse/shared-react.components.icon";
import Number from "@cayuse/shared-react.components.form.inputs.number";
import { AlertDialog } from "@cayuse/shared-react.components.modal-dialog";
import { isMap, isNonEmptyMap } from "@cayuse/shared-react.utils.type-checks";

import { useDictionary } from "hooks";
import { notifyError } from "utils";
import { deepCopy } from "components/flowable/ResearchTeam/utils/helpers";

import { useRuleBuilderContext } from "containers/Rules/context/useRuleBuilderContext";
import { useConditionText } from "../ConditionBuilder/hooks/useConditionText";
import { useActionText } from "../ConditionBuilder/hooks/useActionText";
import { IS_DEV, NEW_RULE_ID } from "globals/constants";

import ActionBuilder from "containers/RulesOld/List/ActionBuilder";
import ConditionBuilder from "containers/RulesOld/List/ConditionBuilder";
import Table from "components/Table";

import styles from "./styles.css";

const OperandDescription = ({ operand }) => useConditionText(operand);

const RuleDescription = ({ rule }) => {
    return (
        <>
            {(rule.condition.operands ?? []).map((group) => (
                <>
                    {group.operator && ` ${group.operator} `}
                    {(group.operands ?? []).map((operand) => (
                        <>
                            {operand.operator && ` ${operand.operator} `}
                            <OperandDescription
                                operand={{
                                    ...operand.operand,
                                    ruleType: operand.operand.optionType,
                                }}
                            />
                        </>
                    ))}
                </>
            ))}
            {"... "}
            {useActionText(rule)}
        </>
    );
};

const RuleRow = ({ rule, hasNewRule }) => {
    const {
        rules,
        setRules,
        removeRule,
        saveRules,
        ruleOrderIndexes,
        setRuleOrderIndexes,
        handleRuleChange,
        setHasUnsavedOrderChanges,
        getNewRuleIndex,
        formChanged,
        setEditingAction,
        setEditingCondition,
        onRuleChange,
        setFormChanged,
        editingCondition,
        isEditingAction,
    } = useRuleBuilderContext();

    const [initialRule, setInitialRule] = useState();
    const [deleteDisabled, setDeleteDisabled] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isConfirmCancelOpen, setConfirmCancelOpen] = useState(false);
    const dictionary = useDictionary("ruleBuilder");

    const isRuleNew = rule.id === NEW_RULE_ID;

    useEffect(() => {
        if (!initialRule || initialRule.id !== rule.id) {
            setInitialRule(deepCopy(rule));
        }
    }, [rule]);

    useEffect(() => {
        setDeleteDisabled(hasNewRule || editingCondition || isEditingAction);
    }, [hasNewRule, editingCondition, isEditingAction]);

    const getRuleCurrentOrderIndex = (rule) => {
        const isNewRuleWithoutIndex =
            rule.id === NEW_RULE_ID &&
            (!isNonEmptyMap(ruleOrderIndexes) ||
                !ruleOrderIndexes.has(NEW_RULE_ID));

        if (isNewRuleWithoutIndex) {
            const newRuleOrder = getNewRuleIndex();
            handleRuleOrderChange(rule, newRuleOrder);

            return newRuleOrder;
        }

        return isMap(ruleOrderIndexes) && ruleOrderIndexes.get(rule.id);
    };

    const handleRuleOrderChange = (rule, newOrderIndex) => {
        const updatedOrderIndexes = isMap(ruleOrderIndexes)
            ? new Map(ruleOrderIndexes)
            : new Map();

        const parsedNewOrderIndex = parseInt(newOrderIndex, 10);
        const finalNewOrderIndex = isFinite(parsedNewOrderIndex)
            ? parsedNewOrderIndex
            : null;

        updatedOrderIndexes.set(rule.id, finalNewOrderIndex);

        setRuleOrderIndexes(updatedOrderIndexes);

        if (rule.id === NEW_RULE_ID) {
            handleRuleChange({ ...rule, orderIndex: newOrderIndex });
        } else {
            setHasUnsavedOrderChanges(true);
        }
    };

    const handleConfirmCancel = () => {
        if (rule.isConditionEditing) {
            if (isRuleNew) {
                const updatedRules = rules.filter(
                    (rule) => rule.id !== NEW_RULE_ID
                );
                setRules(updatedRules);
            } else {
                onRuleChange({
                    ...deepCopy(initialRule),
                    isConditionEditing: false,
                });
            }
            setEditingCondition(false);
            setFormChanged(false);
        } else {
            rule.isActionEditing = false;
            setEditingAction(false);

            if (rule.isAddingMode) {
                rule.isConditionEditing = true;
                setFormChanged(true);
            } else {
                onRuleChange({
                    ...deepCopy(initialRule),
                    isConditionEditing: rule.isAddingMode,
                    isActionEditing: false,
                });
            }
        }

        setConfirmCancelOpen(false);
    };

    const handleRuleCancel = () => {
        if (formChanged) {
            setConfirmCancelOpen(true);
        } else {
            if (rule.isConditionEditing) {
                setEditingCondition(false);
                onRuleChange({
                    ...deepCopy(initialRule),
                    isConditionEditing: false,
                });
            } else {
                setEditingAction(false);
                if (rule.isAddingMode) {
                    rule.isActionEditing = false;
                    rule.isConditionEditing = true;
                    setFormChanged(true);
                } else {
                    onRuleChange({
                        ...deepCopy(initialRule),
                        isConditionEditing: rule.isAddingMode,
                        isActionEditing: false,
                    });
                }
            }
        }
    };

    const handleConditionSave = () => {
        rule.isConditionEditing = false;
        rule.isActionEditing = true;
        rule.isAddingMode = true;
        rule.ruleType = null;
        rule.targetId = null;
        rule.rollupEnabled = false;
        setFormChanged(false);
        setEditingCondition(false);
        setEditingAction(true);
    };

    const handleActionSave = (event) => {
        rule.orderIndex = isNonEmptyMap(ruleOrderIndexes)
            ? ruleOrderIndexes.get(rule.id)
            : null;

        rule.isActionEditing = false;
        rule.isAddingMode = false;

        saveRules(event)
            .then(() => {
                setInitialRule(deepCopy(rule));
            })
            .catch((e) => {
                const userFriendly =
                    "Unable to save the rule, please try again";
                const message = IS_DEV ? e.message : userFriendly;

                notifyError(message, e);
            });
    };

    const handleRuleDelete = () => {
        removeRule(rule.id);
        setDeleteModalOpen(false);
    };

    return (
        <Table.Row key={`rule-${rule.id}`}>
            <Table.Cell className={styles.reviewOrderCol}>
                <Number
                    className={styles.orderInput}
                    key={`rule-order-${rule.id}`}
                    maxLength="2"
                    onChange={(event) =>
                        handleRuleOrderChange(rule, event.target.value)
                    }
                    value={getRuleCurrentOrderIndex(rule)}
                />
            </Table.Cell>
            <Table.Cell>
                <ConditionBuilder
                    rule={rule}
                    isNew={isRuleNew}
                    onRuleChange={handleRuleChange}
                    handleRuleSave={handleConditionSave}
                    handleRuleCancel={handleRuleCancel}
                />
            </Table.Cell>
            <Table.Cell className="top aligned">
                <ActionBuilder
                    rule={rule}
                    isNew={isRuleNew}
                    onRuleChange={handleRuleChange}
                    handleRuleSave={handleActionSave}
                    handleRuleCancel={handleRuleCancel}
                />
            </Table.Cell>
            <Table.Cell className={styles.deleteButton}>
                {!rule.isConditionEditing && !rule.isActionEditing && (
                    <CovertButton
                        type="button"
                        disabled={deleteDisabled}
                        onClick={() => setDeleteModalOpen(true)}
                        aria-label="Remove rule"
                        className={styles.deleteButton}
                    >
                        <Icon
                            name="trash"
                            size="bigger"
                        />
                    </CovertButton>
                )}
            </Table.Cell>
            {isDeleteModalOpen && (
                <AlertDialog
                    title={`${
                        dictionary.conditionBuilder.deleteRule || "Delete Rule"
                    }?`}
                    description={
                        <>
                            Delete the rule{" "}
                            <b>
                                <RuleDescription rule={rule} />
                            </b>
                            ?
                        </>
                    }
                    isOpen={isDeleteModalOpen}
                    onConfirm={handleRuleDelete}
                    confirmText={
                        dictionary.conditionBuilder.deleteRule || "Delete Rule"
                    }
                    onCancel={() => setDeleteModalOpen(false)}
                />
            )}

            {isConfirmCancelOpen && (
                <AlertDialog
                    title={"Discard changes?"}
                    description={
                        dictionary.confirmCancel ||
                        "There are unsaved changes. Are you sure you want to discard your changes?"
                    }
                    isOpen={isConfirmCancelOpen}
                    onConfirm={handleConfirmCancel}
                    confirmText={"Discard Changes"}
                    onCancel={() => setConfirmCancelOpen(false)}
                />
            )}
        </Table.Row>
    );
};

export default RuleRow;
